import * as Yup from "yup";
import validator from "validator";
import * as i18next from "i18next";

import {
  ExtensionField,
  FreeformAmountField,
  LookupField,
} from "types/payable";
import { getLabelWithKrn } from "utilities/helper";
import { getFormattedTime } from "./utils";

// TODO: change to libphonenumber-js library as in MyHQ+.
function validateMobileNumber(value: string | null | undefined) {
  return value ? validator.isMobilePhone(value, "en-US") : true;
}

function getNonblankTextSchema(
  field: ExtensionField,
  t: i18next.TFunction,
  labels: any,
  typeKrn: string | null,
  isRequired: boolean
) {
  let schema = Yup.string();
  if (isRequired) {
    schema = schema.required(
      `${t("nonblanktext-required-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
      })}`
    );
  }
  if ("minLength" in field && field.minLength) {
    schema = schema.min(
      field.minLength,
      t("nonblanktext-min-length-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
        minLength: field.minLength,
      })
    );
  }
  if ("maxLength" in field && field.maxLength) {
    schema = schema.max(
      field.maxLength,
      `${t("nonblanktext-max-length-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
        maxLength: field.maxLength,
      })}`
    );
  }
  return schema;
}

function getAddressSchema(
  field: ExtensionField,
  t: i18next.TFunction,
  labels: any,
  typeKrn: string | null
) {
  return Yup.object({
    streetAddress: Yup.string().required(
      t("mailing-address-required-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, "address"),
      })
    ),
    locality: Yup.string().required(
      t("city-required-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, "city"),
      })
    ),
    region: Yup.string().required(
      t("state-required-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, "state"),
      })
    ),
    postalCode: Yup.string()
      .required(
        t("postal-code-required-error", {
          fieldName: getLabelWithKrn(labels, typeKrn, "postalCode"),
        })
      )
      .matches(
        /^(?:\d{5}|^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\s?\d[ABCEGHJ-NPRSTV-Z]\d)$/i,
        t("postal-code-invalid")
      )
      .max(7, t("postal-code-max-length-error"))
      .min(5, t("postal-code-min-length-error")),
  });
}

function getIntegerSchema(
  field: ExtensionField,
  t: i18next.TFunction,
  labels: any,
  typeKrn: string | null,
  isRequired: boolean
) {
  let schema = Yup.number().typeError(
    `${t("integer-type-error", {
      fieldName: getLabelWithKrn(labels, typeKrn, field.name),
    })}`
  );
  if (isRequired) {
    schema = schema.required(
      `${t("integer-required-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
      })}`
    );
  }
  if ("min" in field && typeof field.min === "number") {
    schema = schema.min(
      field.min,
      t("integer-min-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
        minLength: field.min,
      })
    );
  }
  if ("max" in field && typeof field.max === "number") {
    schema = schema.max(
      field.max,
      `${t("integer-max-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
        maxLength: field.max,
      })}`
    );
  }
  return schema;
}

function getDollarAmountSchema(
  field: ExtensionField | FreeformAmountField,
  t: i18next.TFunction,
  labels: any,
  typeKrn: string | null,
  isRequired: boolean
) {
  let schema = Yup.number().typeError(
    `${t("integer-type-error", {
      fieldName: getLabelWithKrn(labels, typeKrn, field.name),
    })}`
  );
  if (isRequired) {
    schema = schema.required(
      `${t("dollaramount-required-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
      })}`
    );
  }
  const hasMin = "min" in field && typeof field.min === "number";
  const hasMax = "max" in field && typeof field.max === "number";
  if (hasMin && hasMax && field.min === field.max) {
    schema = schema.test(
      "amount-equals",
      t("dollaramount-equal-error", {
        min: field.min,
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
      }),
      (value) => value === field.min
    );
  }
  if (hasMin) {
    schema = schema.min(
      field.min as number,
      t("dollaramount-min-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
        min: field.min,
      })
    );
  }
  if (hasMax) {
    schema = schema.max(
      field.max as number,
      `${t("dollaramount-max-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
        max: field.max,
      })}`
    );
  }
  return schema;
}

function getBooleanSchema(
  field: ExtensionField,
  t: i18next.TFunction,
  labels: any,
  typeKrn: string | null,
  isRequired: boolean
) {
  let schema = Yup.boolean();
  if (isRequired) {
    schema = schema.required(
      `${t("boolean-required-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
      })}`
    );
  }
  return schema;
}

function getEmailSchema(
  field: ExtensionField,
  t: i18next.TFunction,
  labels: any,
  typeKrn: string | null,
  isRequired: boolean
) {
  let schema = Yup.string().email(
    `${t("email-invalid-error", {
      fieldName: getLabelWithKrn(labels, typeKrn, field.name),
    })}`
  );
  if (isRequired) {
    schema = schema.required(
      `${t("email-required-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
      })}`
    );
  }
  return schema;
}

function getPhoneNumberSchema(
  field: ExtensionField,
  t: i18next.TFunction,
  labels: any,
  typeKrn: string | null,
  isRequired: boolean
) {
  let schema = Yup.string().test(
    field.name,
    `${t("phonenumber-invalid-error", {
      fieldName: getLabelWithKrn(labels, typeKrn, field.name),
    })}`,
    (phoneNumber) => validateMobileNumber(phoneNumber)
  );
  if (isRequired) {
    schema = schema.required(
      `${t("phonenumber-required-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
      })}`
    );
  }
  return schema;
}

function getDateSchema(
  field: ExtensionField,
  t: i18next.TFunction,
  labels: any,
  typeKrn: string | null,
  isRequired: boolean
) {
  let schema = Yup.date();
  if (isRequired) {
    schema = schema.required(
      `${t("date-required-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
      })}`
    );
  }
  if ("min" in field && field.min) {
    const minDate = new Date(field.min);
    schema = schema.min(
      minDate,
      t("date-min-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
        min: field.min,
      })
    );
  }
  if ("max" in field && field.max) {
    const maxDate = new Date(field.max);
    schema = schema.max(
      maxDate,
      t("date-max-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
        max: field.max,
      })
    );
  }
  return schema;
}

function getDateTimeSchema(
  field: ExtensionField,
  t: i18next.TFunction,
  labels: any,
  typeKrn: string | null,
  isRequired: boolean
) {
  let dateSchema = Yup.date().test(
    "conditional-required",
    `${t("datetime-date-required-error", {
      fieldName: getLabelWithKrn(labels, typeKrn, field.name),
    })}`,
    function (value) {
      const { time } = this.parent;
      if (time && time.length > 0) {
        return value != null;
      }
      return true;
    }
  );
  let timeSchema = Yup.string()
    .matches(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/)
    .test(
      "conditional-required",
      `${t("datetime-time-required-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
      })}`,
      function (value) {
        const { date } = this.parent;
        if (date != null) {
          return Boolean(value && value.length > 0);
        }
        return true;
      }
    );
  if (isRequired) {
    dateSchema = dateSchema.required(
      `${t("datetime-date-required-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
      })}`
    );
    timeSchema = timeSchema.required(
      `${t("datetime-time-required-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
      })}`
    );
  }
  if ("min" in field && field.min) {
    const minDateTime = new Date(field.min);
    const minDateOnly = new Date(minDateTime.toDateString());
    const minTimeOnly = getFormattedTime(minDateTime);
    dateSchema = dateSchema.min(
      minDateOnly,
      t("datetime-date-min-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
        min: minDateOnly,
      })
    );
    timeSchema = timeSchema.test(
      "min-time",
      t("datetime-time-min-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
        min: minTimeOnly,
      }),
      (value) => (value ? value >= minTimeOnly : !isRequired)
    );
  }
  if ("max" in field && field.max) {
    const maxDateTime = new Date(field.max);
    const maxDateOnly = new Date(maxDateTime.toDateString());
    const maxTimeOnly = getFormattedTime(maxDateTime);
    dateSchema = dateSchema.max(
      maxDateOnly,
      t("datetime-date-max-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
        max: maxDateOnly,
      })
    );
    timeSchema = timeSchema.test(
      "max-time",
      t("datetime-time-max-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
        max: maxTimeOnly,
      }),
      (value) => (value ? value <= maxTimeOnly : !isRequired)
    );
  }
  return Yup.object({
    date: dateSchema,
    time: timeSchema,
  });
}

function getSelectOneSchema(
  field: ExtensionField,
  t: i18next.TFunction,
  labels: any,
  typeKrn: string | null,
  isRequired: boolean
) {
  let schema = Yup.string();
  if (isRequired) {
    schema = schema.required(
      `${t("selectone-required-error", {
        fieldName: getLabelWithKrn(labels, typeKrn, field.name),
      })}`
    );
  }
  return schema;
}

function getSelectManySchema(
  field: ExtensionField,
  t: i18next.TFunction,
  labels: any,
  typeKrn: string | null,
  isRequired: boolean
) {
  let schema = Yup.array().of(Yup.string());
  if (isRequired) {
    schema = schema
      .required(
        `${t("selectmany-required-error", {
          fieldName: getLabelWithKrn(labels, typeKrn, field.name),
        })}`
      )
      .min(
        1,
        `${t("selectmany-required-error", {
          fieldName: getLabelWithKrn(labels, typeKrn, field.name),
        })}`
      );
  }
  return schema;
}

export function generateValidationSchema(
  fields: Array<LookupField | ExtensionField | FreeformAmountField>,
  t: i18next.TFunction,
  labels: any,
  typeKrn: string | null
) {
  const schema = fields.reduce((schema: Record<string, any>, field) => {
    let isRequired = true;
    if ("optionalForPayment" in field) {
      isRequired = !field.optionalForPayment;
    }
    switch (field.type) {
      case "NonblankText":
        schema[field.name] = getNonblankTextSchema(
          field as ExtensionField,
          t,
          labels,
          typeKrn,
          isRequired
        );
        break;
      case "Address":
        schema[field.name] = getAddressSchema(
          field as ExtensionField,
          t,
          labels,
          typeKrn
        );
        break;
      case "Integer":
        schema[field.name] = getIntegerSchema(
          field as ExtensionField,
          t,
          labels,
          typeKrn,
          isRequired
        );
        break;
      case "DollarAmount":
      case "FreeformAmount":
        schema[field.name] = getDollarAmountSchema(
          field as ExtensionField | FreeformAmountField,
          t,
          labels,
          typeKrn,
          isRequired
        );
        break;
      case "Boolean":
        schema[field.name] = getBooleanSchema(
          field as ExtensionField,
          t,
          labels,
          typeKrn,
          isRequired
        );
        break;
      case "Email":
        schema[field.name] = getEmailSchema(
          field as ExtensionField,
          t,
          labels,
          typeKrn,
          isRequired
        );
        break;
      case "PhoneNumber":
        schema[field.name] = getPhoneNumberSchema(
          field as ExtensionField,
          t,
          labels,
          typeKrn,
          isRequired
        );
        break;
      case "Date":
        schema[field.name] = getDateSchema(
          field as ExtensionField,
          t,
          labels,
          typeKrn,
          isRequired
        );
        break;
      case "DateTime":
        schema[field.name] = getDateTimeSchema(
          field as ExtensionField,
          t,
          labels,
          typeKrn,
          isRequired
        );
        break;
      case "SelectOne":
        schema[field.name] = getSelectOneSchema(
          field as ExtensionField,
          t,
          labels,
          typeKrn,
          isRequired
        );
        break;
      case "SelectMany":
        schema[field.name] = getSelectManySchema(
          field as ExtensionField,
          t,
          labels,
          typeKrn,
          isRequired
        );
        break;
      default:
        ((_: never) => {
          throw new Error(`Unhandled case: ${JSON.stringify(field)}`);
        })(field);
    }
    return schema;
  }, {});
  return Yup.object(schema);
}
